import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeftIcon, Pencil01Icon } from '@untitledui-icons/react/line'

import { Button } from '@/components/base/buttons'
import { Modal, ModalContent, ModalFooter, ModalHeader, ModalTitle } from '@/components/ui/Modal'
import { cn } from '@/lib/utils'
import type {
  WidgetTemplate,
  WidgetTemplateKind,
  WidgetTemplateUpsert,
} from '@/hooks/api/useWidgets'
import { WidgetAppearanceFields } from './WidgetAppearanceFields'
import { WidgetToggleCard } from './WidgetEditorControls'
import { WidgetMultiVenueCard } from './WidgetMultiVenueCard'
import { WidgetPreview } from './WidgetPreview'
import { WidgetTemplateAssignees } from './WidgetTemplateAssignees'
import { WidgetTemplateNameModal } from './WidgetTemplateNameModal'
import {
  fromTemplateStyle,
  toTemplateStyle,
  type WidgetStyleFormState,
} from './widgetConfig.serializers'

const TEMPLATES_PATH = '/widgets?tab=templates'

interface WidgetTemplateEditorProps {
  template: WidgetTemplate
  onSave: (payload: WidgetTemplateUpsert) => Promise<void>
  isSaving: boolean
}

/**
 * Styling editor for one saved template. A template is named and created before
 * it gets here, so this screen only ever edits — the name is changed through the
 * pencil, and Save applies the styling to every assistant assigned to it.
 */
export function WidgetTemplateEditor({ template, onSave, isSaving }: WidgetTemplateEditorProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const base = 'resources.widgets'

  const [name, setName] = useState(template.name)
  const [kind, setKind] = useState<WidgetTemplateKind>(template.kind)
  const [style, setStyle] = useState<WidgetStyleFormState>(() => fromTemplateStyle(template.style))
  const [isRenameOpen, setRenameOpen] = useState(false)
  const [isLeaveOpen, setLeaveOpen] = useState(false)

  /** What was last saved, so Save can go quiet once there is nothing to apply. */
  const [saved, setSaved] = useState(() => ({
    name: template.name,
    kind: template.kind,
    style: fromTemplateStyle(template.style),
  }))

  const isDirty =
    name !== saved.name ||
    kind !== saved.kind ||
    JSON.stringify(style) !== JSON.stringify(saved.style)

  const venues = useMemo(
    () =>
      [...template.assignments]
        .sort((first, second) => first.assistant_name.localeCompare(second.assistant_name))
        .map((assignment) => ({
          id: assignment.assistant_id,
          name: assignment.assistant_name,
          address: assignment.address,
        })),
    [template.assignments]
  )

  const isMultiVenue = kind === 'multi'
  const iconOnly = !isMultiVenue && style.iconOnly

  const updateStyle = (patch: Partial<WidgetStyleFormState>) => {
    setStyle((current) => ({ ...current, ...patch }))
  }

  const handleKindChange = (nextKind: WidgetTemplateKind) => {
    setKind(nextKind)
    if (nextKind === 'multi' && style.iconOnly) {
      updateStyle({ iconOnly: false })
    }
  }

  const handleSave = async (): Promise<boolean> => {
    const nextStyle = isMultiVenue ? { ...style, iconOnly: false } : style

    try {
      await onSave({ name, kind, style: toTemplateStyle(nextStyle) })
    } catch {
      // The page reports the failure; staying dirty keeps the edits and the retry.
      return false
    }

    setStyle(nextStyle)
    setSaved({ name, kind, style: nextStyle })
    return true
  }

  const handleBack = () => {
    if (isDirty) {
      setLeaveOpen(true)
      return
    }

    navigate(TEMPLATES_PATH)
  }

  return (
    <div className="flex flex-col">
      <header className="border-b border-gray-200 px-6 py-6 xl:px-16 dark:border-gray-700">
        <button
          type="button"
          aria-label={t(`${base}.editor.back`)}
          onClick={handleBack}
          className="mb-6 inline-flex w-fit text-gray-900 dark:text-gray-100"
        >
          <ArrowLeftIcon className="size-6" />
        </button>

        <div className="flex flex-wrap items-center justify-between gap-6">
          <div className="flex items-center gap-2">
            <h1 className="text-[30px] leading-[38px] font-medium text-gray-900 dark:text-gray-100">
              {name}
            </h1>
            <button
              type="button"
              aria-label={t(`${base}.editor.rename`)}
              onClick={() => setRenameOpen(true)}
              className="flex size-8 items-center justify-center rounded-sm text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
            >
              <Pencil01Icon className="size-[18px]" />
            </button>
          </div>

          <Button
            color={isDirty ? 'primary' : 'secondary'}
            size="sm"
            isDisabled={!isDirty}
            isLoading={isSaving}
            className={cn('h-9 rounded-md px-4', !isDirty && 'text-gray-500 dark:text-gray-400')}
            onPress={() => void handleSave()}
          >
            {t(`${base}.editor.save`)}
          </Button>
        </div>
      </header>

      <div className="flex flex-col items-stretch xl:flex-row">
        <div className="flex w-full flex-col gap-10 px-6 py-6 xl:min-h-[640px] xl:w-[600px] xl:shrink-0 xl:border-r xl:border-gray-200 xl:px-16 dark:xl:border-gray-700">
          <WidgetAppearanceFields state={style} onChange={updateStyle} />

          <WidgetToggleCard
            title={t(`${base}.appearance.icon_only_title`)}
            description={
              isMultiVenue
                ? t(`${base}.appearance.icon_only_disabled_description`)
                : t(`${base}.appearance.icon_only_description`)
            }
            isSelected={iconOnly}
            isDisabled={isMultiVenue}
            onChange={(nextIconOnly) => updateStyle({ iconOnly: nextIconOnly })}
          />

          <WidgetTemplateAssignees template={template} />

          <WidgetMultiVenueCard
            kind={kind}
            onKindChange={handleKindChange}
            assignments={template.assignments}
          />
        </div>

        {/*
          Stacked under the form it shares the form's gutter; as the right column
          it takes the design's narrower inset and whatever width the form leaves,
          which the mock inside scales down to. Beside the form the mock sticks to
          the top of the viewport, so it stays in view while the form scrolls.
        */}
        <div className="bg-gray-25 w-full px-6 py-6 xl:min-h-[calc(100vh-135px)] xl:flex-1 xl:px-8 dark:bg-gray-900/40">
          <div className="xl:sticky xl:top-6">
            <WidgetPreview
              style={{ ...style, iconOnly }}
              venues={venues}
              isMultiVenue={isMultiVenue}
            />
          </div>
        </div>
      </div>

      <WidgetTemplateNameModal
        isOpen={isRenameOpen}
        initialName={name}
        onClose={() => setRenameOpen(false)}
        onConfirm={(nextName) => {
          setName(nextName)
          setRenameOpen(false)
        }}
      />

      <Modal isOpen={isLeaveOpen} onOpenChange={setLeaveOpen} size="md">
        <ModalContent>
          <ModalHeader onClose={() => setLeaveOpen(false)} bordered={false}>
            <div className="space-y-1">
              <ModalTitle>{t(`${base}.editor.unsaved_title`)}</ModalTitle>
              <p className="text-[13px] leading-[18px] text-gray-600 dark:text-gray-400">
                {t(`${base}.editor.unsaved_description`)}
              </p>
            </div>
          </ModalHeader>

          <ModalFooter bordered={false} align="stretch">
            <Button color="secondary" size="lg" onPress={() => navigate(TEMPLATES_PATH)}>
              {t(`${base}.editor.discard`)}
            </Button>
            <Button
              color="primary"
              size="lg"
              isLoading={isSaving}
              onPress={async () => {
                if (await handleSave()) {
                  navigate(TEMPLATES_PATH)
                }
              }}
            >
              {t(`${base}.editor.save_changes`)}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  )
}
